import { useState, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { ARCHIVE_CHAT_GLOBAL_CONTEXT } from '../utils/archiveChatContext';

export type ChatMessage = {
  id: string;
  text: string;
  isUser: boolean;
};

export enum AskArchiveError {
  NoDocuments = 'no_documents',
  OpenAIError = 'openai_error',
}

export function useArchiveChat(additionalContext?: string, documentId?: string, projectId?: string, knowledgeId?: string) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [question, setQuestion] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [errorState, setErrorState] = useState<AskArchiveError | null>(null);

  const sendMessage = useCallback(async () => {
    if (!question.trim() || isThinking) return;

    const userQuestion = question.trim();
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), text: userQuestion, isUser: true }]);
    setQuestion('');
    setIsThinking(true);
    setErrorState(null);

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      setErrorState(AskArchiveError.OpenAIError);
      setIsThinking(false);
      return;
    }

    try {
      // Build additional context from previous messages and any provided context
      const contextFromMessages = messages.length > 0 
        ? messages.map(msg => `${msg.isUser ? 'User' : 'Assistant'}: ${msg.text}`).join('\n\n')
        : '';
      
      // Always include global context, then any additional context, then conversation history
      const combinedAdditionalContext = [
        ARCHIVE_CHAT_GLOBAL_CONTEXT,
        additionalContext,
        contextFromMessages
      ]
        .filter(Boolean)
        .join('\n\n');

      // Call the askKnowledge cloud function
      const functionUrl = process.env.NEXT_PUBLIC_ASK_KNOWLEDGE_URL || 
        'https://askknowledge-3l53kqimxq-ey.a.run.app';
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: 'grdKljB3XuW5iVSqQl3N6VDfvi53', // Always use AI knowledge account
          question: userQuestion,
          topK: 8,
          additionalContext: combinedAdditionalContext || undefined,
          documentId: documentId || undefined,
          projectId: projectId || undefined,
          knowledgeId: knowledgeId || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.answer) {
        setMessages((prev) => [...prev, { id: crypto.randomUUID(), text: result.answer, isUser: false }]);
      } else {
        throw new Error('No answer received from knowledge service');
      }
    } catch (err: unknown) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Knowledge request failed', err);
      }
      setErrorState(AskArchiveError.OpenAIError);
      setMessages((prev) => [...prev, { 
        id: crypto.randomUUID(), 
        text: err instanceof Error ? `Error: ${err.message}` : 'Failed to get response from knowledge assistant.', 
        isUser: false 
      }]);
    } finally {
      setIsThinking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, isThinking, messages, documentId, projectId, knowledgeId]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setErrorState(null);
  }, []);

  return {
    messages,
    question,
    setQuestion,
    sendMessage,
    isThinking,
    errorState,
    clearMessages,
  };
}
